import { Injectable } from "@angular/core";
import { Subject } from "rxjs";
import { MediaRepository } from "../../domain/media-repository";
import { Socket } from "../socket.service";
import { HttpService } from "../http/http";
import { UserService } from "../../domain/user-service";
import { MediaLinkedType } from "../../domain/media";
import { GraphicsRepository } from "../../domain/graphics-repository";
import { TenantStorageOption } from "../../domain/tenant-storage-option";
import { IUploadFile } from "../../domain/Upload/i-upload-file";
import { ICloudStorageProvider } from "../../domain/Upload/i-cloud-storage-provider";
import { ICloudStorageProviderFactory } from "../../domain/Upload/i-cloud-storage-provider-factory";
import { Logger } from "../logger";
import { Auth } from "../auth";
import { HttpClient } from "@angular/common/http";
import { S3CloudProvider } from "./s3-cloud-provider";
import { v4 as uuidv4 } from "uuid";

function getFileExtension(file: File) {
  return file.name.split(".").pop();
}

@Injectable()
export class UploadService {
  private linkedFiles: { [mediaId: string]: IUploadFile[] } = {};
  private packshots: { [mediaId: string]: IUploadFile } = {};
  private mediaFiles: IUploadFile[] = [];
  private graphicFile: string;
  private thumbnailFile: string;
  private fontFile: string;
  private sampleVideoFile: string;

  linkedFileComplete = new Subject<{
    uploadId: string;
    mediaId: number;
    mediaUid: string;
    mediaLinkedType: MediaLinkedType;
    parentMediaId: number;
    name: string;
  }>();
  linkedFileError = new Subject<{
    parentMediaId: number;
    mediaLinkedType: MediaLinkedType;
    uploadId: string;
  }>();

  mediaComplete = new Subject<{
    uploadId: string;
    mediaId: number;
    mediaUid: string;
  }>();
  mediaError = new Subject<{ uploadId: string }>();

  packshotComplete = new Subject<{ mediaId: number; uploadId: string }>();
  packshotError = new Subject<{ mediaId: number; uploadId: string }>();

  graphicsComplete = new Subject<any>();
  graphicsError = new Subject<any>();

  thumbnailComplete = new Subject<any>();
  thumbnailError = new Subject<any>();

  fontComplete = new Subject<any>();
  fontError = new Subject<any>();

  sampleVideoComplete = new Subject<any>();
  sampleVideoError = new Subject<any>();

  batchFileReadyForUpload = new Subject<any>();
  uploadingEvent = new Subject<any>();

  tenantPrefix;

  batchUploadFiles: IUploadFile[] = [];

  constructor(
    private cloudStorageProviderFactory: ICloudStorageProviderFactory,
    private mediaRepository: MediaRepository,
    private socketService: Socket,
    private httpService: HttpService,
    private userService: UserService,
    private graphicsRepository: GraphicsRepository,
    private logService: Logger,
    private authService: Auth,
    private httpClient: HttpClient,
  ) {
    this.tenantPrefix = userService.getCurrentUser().tenantGuid;
  }

  async uploadFileToTenantBucket(
    path: string,
    file: File,
    tenantStorageOption: TenantStorageOption,
  ) {
    const fileName = `${path ? path + "/" : ""}${file.name}`;

    // only S3

    const uploadFile = await new S3CloudProvider(
      this.httpService,
      this.userService,
      this.logService,
      this.authService,
    ).createFileForTenantBucket(uuidv4(), file, fileName, tenantStorageOption);

    this.batchUploadFiles.push(uploadFile);
    this.batchFileReadyForUpload.next(true);
    uploadFile.start();
    return uploadFile;
  }

  uploadFontFile(file: File, fontName: string) {
    const fileName = `${
      this.userService.getCurrentUser().tenantGuid
    }/fonts/${uuidv4()}${file.name}`;
    const notifyPromise = (objectKey) => {
      return this.graphicsRepository.fontUpload(objectKey, fontName);
    };

    const uploadFontFile = this.initCloudStorageProvider().createFile(
      uuidv4(),
      file,
      fileName,
      "graphicsFont",
      notifyPromise,
    );

    uploadFontFile.isCompleteChange.subscribe((result) => {
      this.fontComplete.next(result);
      this.fontFile = null;
    });

    uploadFontFile.isErrorChange.subscribe((error) => {
      this.fontError.next(error);
      this.fontFile = null;
    });

    uploadFontFile.start();
    return uploadFontFile;
  }

  uploadGraphicsSampleVideo(file: File, graphicsId: number) {
    const fileName = `${
      this.userService.getCurrentUser().tenantGuid
    }/graphics/sampleVideos/${graphicsId}/${file.name}`;
    const notifyPromise = (objectKey) => {
      return this.graphicsRepository.sampleVideoUpload(
        objectKey,
        file.name,
        graphicsId,
      );
    };

    const uploadFontFile = this.initCloudStorageProvider().createFile(
      uuidv4(),
      file,
      fileName,
      "graphicsSampleVideo",
      notifyPromise,
    );

    uploadFontFile.isCompleteChange.subscribe((result) => {
      this.sampleVideoComplete.next(result);
      this.sampleVideoFile = null;
    });

    uploadFontFile.isErrorChange.subscribe((error) => {
      this.sampleVideoError.next(error);
      this.sampleVideoFile = null;
    });

    uploadFontFile.start();
    return uploadFontFile;
  }

  uploadGraphicsThumbnailFile(file: File, graphicsId: number) {
    const fileName = `${
      this.userService.getCurrentUser().tenantGuid
    }/graphics/thumbnails/${graphicsId}/${uuidv4()}${file.name}`;
    const notifyPromise = (objectKey) => {
      return this.graphicsRepository.thumbnailUpload(
        objectKey,
        file.name,
        graphicsId,
      );
    };

    const uploadThumbnailFile = this.initCloudStorageProvider().createFile(
      uuidv4(),
      file,
      fileName,
      "graphicsThumbnails",
      notifyPromise,
    );

    uploadThumbnailFile.isCompleteChange.subscribe((result) => {
      this.thumbnailComplete.next(result);
      this.thumbnailFile = null;
    });

    uploadThumbnailFile.isErrorChange.subscribe((error) => {
      this.thumbnailError.next(error);
      this.thumbnailFile = null;
    });

    uploadThumbnailFile.start();
    return uploadThumbnailFile;
  }

  uploadGraphicsFile(
    file: File,
    graphicsName: string,
    graphicsDescription: string,
  ) {
    const fileName = `${
      this.userService.getCurrentUser().tenantGuid
    }/graphics/packages/${uuidv4()}.zip`;
    const notifyPromise = (objectKey) => {
      return this.graphicsRepository.graphicsUpload(
        objectKey,
        file.name,
        graphicsName,
        graphicsDescription,
      );
    };
    const uploadGraphicsFile = this.initCloudStorageProvider().createFile(
      uuidv4(),
      file,
      fileName,
      "graphics",
      notifyPromise,
    );
    uploadGraphicsFile.isCompleteChange.subscribe((result) => {
      this.graphicsComplete.next(result);
      this.graphicFile = null;
    });

    uploadGraphicsFile.isErrorChange.subscribe((error) => {
      this.graphicsError.next(error);
      this.graphicFile = null;
    });

    uploadGraphicsFile.start();
    return uploadGraphicsFile;
  }

  isUploadingGraphics() {
    return !!this.graphicFile;
  }

  isUploadingThumbnail() {
    return !!this.thumbnailFile;
  }

  isUploadingSampleVideo() {
    return !!this.sampleVideoFile;
  }

  uploadClosedCaption(file: File, mediaId: number): Promise<any> {
    const fileName = `${
      this.userService.getCurrentUser().tenantGuid
    }/medias/${mediaId}/closedCaptions/${uuidv4()}.${getFileExtension(file)}`;

    return new Promise((resolve, reject) => {
      const notifyPromise = (objectKey) => {
        return this.mediaRepository.uploadClosedCaption(
          objectKey,
          file.name,
          mediaId,
        );
      };

      const uploadFile = this.initCloudStorageProvider().createTranscodedFile(
        uuidv4(),
        file,
        fileName,
        notifyPromise,
      );

      uploadFile.isCompleteChange.subscribe((result) => {
        resolve(result);
      });

      uploadFile.isErrorChange.subscribe((err) => {
        reject(err);
      });

      uploadFile.start();
    });
  }

  uploadLinkedFile(
    file: File,
    parentMediaId: number,
    mediaLinkedType: MediaLinkedType,
  ) {
    const fileName = `${
      this.userService.getCurrentUser().tenantGuid
    }/medias/${parentMediaId}/${MediaLinkedType[
      mediaLinkedType
    ].toLowerCase()}/${uuidv4()}.${getFileExtension(file)}`;
    const notifyPromise = (objectKey) => {
      return this.mediaRepository.contentUpload(
        objectKey,
        file.name,
        this.socketService.connectionId,
        parentMediaId,
        mediaLinkedType,
      );
    };

    const uploadFile = this.initCloudStorageProvider().createOriginalMediaFile(
      uuidv4(),
      file,
      fileName,
      notifyPromise,
      { mediaLinkedType: mediaLinkedType },
    );
    if (!this.linkedFiles[parentMediaId]) {
      this.linkedFiles[parentMediaId] = [];
    }
    this.linkedFiles[parentMediaId].push(uploadFile);
    uploadFile.abortedChange.subscribe(() => {
      this.removeLinkedFile(uploadFile, parentMediaId);
    });
    uploadFile.isCompleteChange.subscribe((result) => {
      this.linkedFileComplete.next({
        uploadId: uploadFile.id,
        mediaId: result.mediaId,
        mediaUid: result.mediaUid,
        mediaLinkedType: uploadFile.customData.mediaLinkedType,
        parentMediaId: parentMediaId,
        name: file.name,
      });
      this.removeLinkedFile(uploadFile, parentMediaId);
    });

    uploadFile.isErrorChange.subscribe(() => {
      if (!uploadFile.isAborted) {
        this.linkedFileError.next({
          parentMediaId: parentMediaId,
          mediaLinkedType: uploadFile.customData.mediaLinkedType,
          uploadId: uploadFile.id,
        });
      }
    });

    uploadFile.start();
    return uploadFile;
  }

  removeLinkedFile(uploadFile: IUploadFile, mediaId: number) {
    const index = this.linkedFiles[mediaId].indexOf(uploadFile);
    if (index > -1) {
      this.linkedFiles[mediaId].splice(index, 1);
    }
  }

  getLinkedFilesOf(mediaId: number, mediaLinkedType: MediaLinkedType) {
    return (this.linkedFiles[mediaId] || []).filter(
      (x) => x.customData.mediaLinkedType === mediaLinkedType,
    );
  }

  uploadPackshot(file: File, mediaId: number) {
    if (this.packshots[mediaId]) {
      throw new Error("packshot is already uploaded");
    }
    const fileName = `${
      this.userService.getCurrentUser().tenantGuid
    }/medias/${mediaId}/packshots/${uuidv4()}.${getFileExtension(file)}`;
    const notifyPromise = (objectKey) => {
      return this.mediaRepository.packshotUpload(
        objectKey,
        this.socketService.connectionId,
        mediaId,
      );
    };

    const uploadFile = this.initCloudStorageProvider().createFile(
      uuidv4(),
      file,
      fileName,
      "thumbnail",
      notifyPromise,
      { mediaId: mediaId },
    );
    this.packshots[mediaId] = uploadFile;

    uploadFile.abortedChange.subscribe(() => {
      this.removePackshotFile(mediaId);
    });
    uploadFile.isCompleteChange.subscribe(() => {
      this.packshotComplete.next({
        uploadId: uploadFile.id,
        mediaId: mediaId,
      });
      this.removePackshotFile(mediaId);
    });

    uploadFile.isErrorChange.subscribe(() => {
      if (!uploadFile.isAborted) {
        this.packshotError.next({
          mediaId: mediaId,
          uploadId: uploadFile.id,
        });
      }
    });

    uploadFile.start();
    return uploadFile;
  }

  getPackshotOf(mediaId: number) {
    return this.packshots[mediaId];
  }

  removePackshotFile(mediaId) {
    delete this.packshots[mediaId];
  }

  uploadMedia(file: File, contractId?: number, customProps?: any) {
    const fileName = `${
      this.userService.getCurrentUser().tenantGuid
    }/${uuidv4()}.${getFileExtension(file)}`;

    const notifyPromise = (objectKey) => {
      return this.mediaRepository.contentUpload(
        objectKey,
        file.name,
        this.socketService.connectionId,
        null,
        null,
        contractId,
        customProps,
      );
    };

    const uploadFile = this.initCloudStorageProvider().createOriginalMediaFile(
      uuidv4(),
      file,
      fileName,
      notifyPromise,
    );

    this.mediaFiles.push(uploadFile);
    this.uploadingEvent.next(true);
    uploadFile.abortedChange.subscribe(() => {
      this.removeMediaFile(uploadFile);
      this.uploadingEvent.next(true);
    });
    uploadFile.isCompleteChange.subscribe((result) => {
      this.mediaComplete.next({
        uploadId: uploadFile.id,
        mediaId: result.mediaId,
        mediaUid: result.mediaUid,
      });
      this.removeMediaFile(uploadFile);
    });

    uploadFile.isErrorChange.subscribe(() => {
      if (!uploadFile.isAborted) {
        this.mediaError.next({ uploadId: uploadFile.id });
      }
    });

    uploadFile.start();
    return uploadFile;
  }

  getUploadedMediaFiles() {
    return this.mediaFiles.slice(0);
  }

  removeMediaFile(uploadFile) {
    const index = this.mediaFiles.indexOf(uploadFile);
    if (index > -1) {
      this.mediaFiles.splice(index, 1);
    }
  }

  uploadSmallFile(file: File, progressCb?: (number) => void): Promise<string> {
    const fileName = `${
      this.userService.getCurrentUser().tenantGuid
    }/${uuidv4()}.${getFileExtension(file)}`;
    let uploadType: any = "thumbnail";

    /*if (this.initCloudStorageProvider().isCustomServiceUrl()) {
          uploadType = 'mediaContent';
        }*/

    return new Promise((resolve, reject) => {
      const notifyPromise = (objectKey) => {
        // console.log('notifyPromise ', objectKey);
        return Promise.resolve(`${objectKey}`);
      };
      const uploadFile = this.initCloudStorageProvider().createFile(
        uuidv4(),
        file,
        fileName,
        uploadType,
        notifyPromise,
      );
      uploadFile.start();
      uploadFile.isCompleteChange.subscribe((url) => {
        console.log("uploadSmallFile -> Completed, URL: ", url);
        resolve(url);
      });

      uploadFile.isErrorChange.subscribe((err) => {
        reject(err);
      });
    });
  }

  private initCloudStorageProvider(): ICloudStorageProvider {
    return this.cloudStorageProviderFactory.createInstance(
      this.httpService,
      this.userService,
      this.logService,
      this.authService,
      this.httpClient,
    );
  }
}
